<template>
    <div>
        <form class="fields" v-on:submit.prevent="onSubmit" autocomplete="off">
            <div class="field">
                <!-- password label -->
                <label for="password-input">
                    {{ $t(`${langRoot}.passwordInputLabel`) }}
                    <i8-icon
                        icon="circle-help"
                        class="password-tips-icon"
                        @click="togglePasswordTipsModal"
                    />
                </label>

                <!-- password input -->
                <input
                    id="password-input"
                    type="password"
                    class="input short"
                    :class="{ error: showPasswordFeedback }"
                    v-focus="defaultFocus"
                    v-model="password.value"
                    @focus="password.requirements.show = true"
                    @keyup="emitPassword"
                    @blur="
                        password.isDirty =
                            true && $trackEvent(pageTag, 'password_entered')
                    "
                    data-ci="new-password-input"
                    aria-required="true"
                    :aria-invalid="!isPasswordStrong"
                    :aria-describedby="
                        password.requirements.show && !showPasswordFeedback
                            ? 'pwdRequirements'
                            : 'pwdFeedback'
                    "
                    autocomplete="off"
                />

                <!-- password requirements message -->
                <page-notification
                    v-if="password.requirements.show"
                    id="pwdRequirements"
                    :messages="$t(`${langRoot}.passwordRules.rule`)"
                    :name="$t(`${langRoot}.passwordRules.title`)"
                    type="blank"
                    class="form-helper"
                />

                <!-- password invalid message -->
                <div
                    id="pwdFeedback"
                    class="form-feedback"
                    role="alert"
                    v-if="showPasswordFeedback"
                >
                    <div class="page-notification__icon">
                        <i8-icon size="small" icon="circle-error-solid" />
                    </div>
                    <span>{{ passwordInadequate }}</span>
                </div>
            </div>

            <div class="field">
                <!-- password confirmation label -->
                <label
                    for="password-confirmation-input"
                    v-t="`${langRoot}.confirmPasswordInputLabel`"
                />

                <!-- password confirmation input -->
                <input
                    id="password-confirmation-input"
                    type="password"
                    class="input short"
                    :class="{ error: showConfirmationFeedback }"
                    v-model="confirmation.value"
                    @focus="password.requirements.show = true"
                    @keyup="emitPassword"
                    @blur="
                        confirmation.isDirty =
                            true &&
                            $trackEvent(pageTag, 'password_confirmation_entered')
                    "
                    autocomplete="off"
                    aria-required="true"
                    :aria-invalid="!isPasswordConfirmed"
                    aria-describedby="confirmationFeedback"
                    data-ci="confirmation-password-input"
                />

                <!-- password confirmation invalid message -->
                <div
                    v-if="showConfirmationFeedback"
                    id="confirmationFeedback"
                    class="form-feedback"
                    role="alert"
                >
                    <div class="page-notification__icon">
                        <i8-icon size="small" icon="circle-error-solid" />
                    </div>
                    <span>{{ passwordsNotMatch }}</span>
                </div>
            </div>
        </form>

        <!-- password advice modal -->
        <i8-modal
            v-if="showPasswordTips"
            :is-open="showPasswordTips"            
            :title="$t(`${langRoot}.securityAdvice.title`)"
        >
            <template slot="i8-modal-body">
                <div class="password-tips copy">
                    <!-- Security Advice Content -->
                    <template
                        v-for="(item, index) in $t(
                            `${langRoot}.securityAdvice.content`
                        )"
                    >
                        <!-- Title item -->
                        <strong v-if="item.title" :key="`title-${index}`">{{
                            item.title
                        }}</strong>

                        <!-- Text item -->
                        <p v-if="item.text" :key="`title-${index}`">
                            {{ item.text }}
                        </p>

                        <!-- list of text items -->
                        <template v-if="item.textList">
                            <ul :key="`text-list-${index}`">
                                <li
                                    v-for="textListItem in item.textList"
                                    :key="`text-list-item-${index}-${textListItem}`"
                                >
                                    {{ textListItem }}
                                </li>
                            </ul>
                        </template>
                    </template>
                </div>
            </template>
            <template slot="i8-modal-footer">
                <div class="level is-block">
                    <div class="level-right is-pulled-right">
                        <button
                            class="btn primary"
                            @click="togglePasswordTipsModal"
                            v-t="`${langRoot}.securityAdvice.returnBtn`"
                        />
                    </div>   
                </div>
            </template>
        </i8-modal>
    </div>
</template>

<script>
import { isEmpty } from 'lodash';
import PageNotification from '@components/PageNotification';
import I8Modal from '@components/I8Modal';
import tracking from '@shared/mixins/tracking';
import I8Icon from '@shared/components/I8Icon';

const name = 'new-password';
export default {
    name,
    componentName: name,

    components: {
        [PageNotification.name]: PageNotification,
        [I8Modal.name]: I8Modal,
        [I8Icon.name]: I8Icon,
    },

    mixins: [tracking],

    props: {
        value: { type: String, default: '' },
        defaultFocus: { type: Boolean, default: false },
        langRoot: { type: String, required: true },
    },

    data() {
        return {
            showPasswordTips: false,
            password: {
                value: '',
                requirements: {
                    show: false,
                    pattern: /^(?=.*[A-Za-z\d@_\-'.])(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@_\-'.])[A-Za-z\d@_\-'.]{8,30}$/,
                },
                feedback: {
                    show: false,
                },
                isDirty: false,
            },

            confirmation: {
                value: '',
                feedback: {
                    show: false,
                },
                isDirty: false,
            },
        };
    },

    computed: {
        showPasswordFeedback() {
            if (isEmpty(this.password.value) || !this.password.feedback.show) {
                return false;
            }

            return !this.isPasswordStrong && this.password.isDirty;
        },

        showConfirmationFeedback() {
            if (
                isEmpty(this.confirmation.value) ||
                !this.confirmation.feedback.show
            ) {
                return false;
            }
            return !this.isPasswordConfirmed && this.confirmation.isDirty;
        },

        isPasswordStrong() {
            let password = this.password.value;
            let pattern = this.password.requirements.pattern;

            return this._isRegexMatch(password, pattern);
        },

        isPasswordConfirmed() {
            if (isEmpty(this.confirmation.value)) {
                return false;
            }

            return this.password.value === this.confirmation.value;
        },

        passwordInadequate() {
            return this.$t(`${this.langRoot}.passwordRuleFail`);
        },

        passwordsNotMatch() {
            return this.$t(`${this.langRoot}.passwordMatchFail`);
        },
    },

    methods: {
        emitPassword() {
            this.password.requirements.show = false;
            this.password.feedback.show = !isEmpty(this.password.value);
            this.confirmation.feedback.show = !isEmpty(this.confirmation.value);

            if (isEmpty(this.password.value)) {
                return;
            }

            if (isEmpty(this.confirmation.value)) {
                return;
            }

            if (!this.isPasswordStrong) {
                return;
            }

            if (!this.isPasswordConfirmed) {
                return;
            }

            this.$emit('input', this.password.value);
        },

        togglePasswordTipsModal() {
            this.showPasswordTips = !this.showPasswordTips;
        },

        _isRegexMatch(str, pattern) {
            if (isEmpty(str)) {
                return false;
            }

            return pattern.test(str);
        },
    },

    watch: {
        showPasswordFeedback(newVal) {
            if (newVal === true) {
                // Use global, this is a generic object for errors
                this.$trackEvent('global', 'field_error', [
                    'Password',
                    this.passwordInadequate,
                ]);
            }
        },

        showConfirmationFeedback(newVal) {
            if (newVal === true) {
                // Use global, this is a generic object for errors
                this.$trackEvent('global', 'field_error', [
                    'Confirm Password',
                    this.passwordsNotMatch,
                ]);
            }
        },
    },
};
</script>



// WEBPACK FOOTER //
// shared/components/NewPassword.vue